import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { membersAPI, unitsAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, MagnifyingGlass, Eye, PencilSimple, Trash, User, Upload, FileText, Download, X, Camera, CalendarBlank, Envelope, Phone, MapPin, Briefcase, Buildings, Medal, Key, UserCircle } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RANKS = ['Novice', 'Sergeant', 'Knight', 'Knight Commander', 'Knight Grand Cross', 'Grand Officer'];
const STATUSES = ['active', 'inactive', 'emeritus', 'deceased'];
const SYSTEM_ROLES = [
  { value: 'member', label: 'Member (View Only)' },
  { value: 'officer', label: 'Officer (Limited Access)' },
  { value: 'admin', label: 'Admin (Full Access)' }
];

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRank, setFilterRank] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCV, setUploadingCV] = useState(false);
  const photoInputRef = useRef(null);
  const cvInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '', title_prefix: '', rank: 'Novice', office: '',
    jurisdiction: '', primary_commandery: '', primary_prefecture: '',
    investiture_date: '', status: 'active', biography: '',
    email: '', phone: '', address: '', internal_notes: '',
    participation_level: 'active', date_of_birth: '', nationality: '',
    occupation: '', emergency_contact: '', emergency_phone: '',
    create_account: false, password: '', system_role: 'member'
  });

  const fetchData = async () => {
    try {
      const [membersRes, unitsRes] = await Promise.all([
        membersAPI.getAll(),
        unitsAPI.getAll()
      ]);
      setMembers(membersRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredMembers = members.filter(m => {
    const matchesSearch = m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         m.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || m.status === filterStatus;
    const matchesRank = filterRank === 'all' || m.rank === filterRank;
    return matchesSearch && matchesStatus && matchesRank;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await membersAPI.update(editingMember.id, formData);
        toast.success('Member updated successfully');
      } else {
        await membersAPI.create(formData);
        toast.success('Member created successfully');
      }
      setShowModal(false);
      setEditingMember(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name || '',
      title_prefix: member.title_prefix || '',
      rank: member.rank || 'Knight',
      office: member.office || '',
      jurisdiction: member.jurisdiction || '',
      primary_commandery: member.primary_commandery || '',
      primary_prefecture: member.primary_prefecture || '',
      investiture_date: member.investiture_date || '',
      status: member.status || 'active',
      biography: member.biography || '',
      email: member.email || '',
      phone: member.phone || '',
      address: member.address || '',
      internal_notes: member.internal_notes || '',
      participation_level: member.participation_level || 'active',
      date_of_birth: member.date_of_birth || '',
      nationality: member.nationality || '',
      occupation: member.occupation || '',
      emergency_contact: member.emergency_contact || '',
      emergency_phone: member.emergency_phone || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to delete ${member.full_name}?`)) return;
    try {
      await membersAPI.delete(member.id);
      toast.success('Member deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handlePhotoUpload = async (e, memberId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingPhoto(true);
    try {
      await membersAPI.uploadPhoto(memberId, file);
      toast.success('Photo uploaded successfully');
      fetchData();
      // Refresh viewing member if in view modal
      if (viewingMember?.id === memberId) {
        const updated = await membersAPI.get(memberId);
        setViewingMember(updated.data);
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCVUpload = async (e, memberId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCV(true);
    try {
      await membersAPI.uploadCV(memberId, file);
      toast.success('CV uploaded successfully');
      fetchData();
      // Refresh viewing member if in view modal
      if (viewingMember?.id === memberId) {
        const updated = await membersAPI.get(memberId);
        setViewingMember(updated.data);
      }
    } catch (error) {
      toast.error('Failed to upload CV');
    } finally {
      setUploadingCV(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '', title_prefix: '', rank: 'Novice', office: '',
      jurisdiction: '', primary_commandery: '', primary_prefecture: '',
      investiture_date: '', status: 'active', biography: '',
      email: '', phone: '', address: '', internal_notes: '',
      participation_level: 'active', date_of_birth: '', nationality: '',
      occupation: '', emergency_contact: '', emergency_phone: '',
      create_account: false, password: '', system_role: 'member'
    });
  };

  const commanderies = units.filter(u => u.type === 'commandery');
  const prefectories = units.filter(u => u.type === 'prefectory');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="members-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Members</h1>
            <p className="text-[#8A847A]">{members.length} total members</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setEditingMember(null); setShowModal(true); }}
            className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none"
            data-testid="add-member-button"
          >
            <Plus size={18} className="mr-2" />
            Add Member
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 bg-white border border-[#D8D2C4] p-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A847A]" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none border-[#D8D2C4]"
              data-testid="member-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 rounded-none" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterRank} onValueChange={setFilterRank}>
            <SelectTrigger className="w-40 rounded-none" data-testid="filter-rank">
              <SelectValue placeholder="Rank" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ranks</SelectItem>
              {RANKS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#D8D2C4] overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <table className="w-full data-table" data-testid="members-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Rank</th>
                  <th className="px-4 py-3 text-left">Office</th>
                  <th className="px-4 py-3 text-left">Commandery</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map(member => (
                  <tr key={member.id} data-testid={`member-row-${member.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#F5F2EB] flex items-center justify-center text-[#8A847A] overflow-hidden">
                          {member.photo_url ? (
                            <img 
                              src={`${API}${member.photo_url}`} 
                              alt={member.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User size={20} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1614]">
                            {member.title_prefix && `${member.title_prefix} `}{member.full_name}
                          </p>
                          {member.email && <p className="text-sm text-[#8A847A]">{member.email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#4A453E]">{member.rank}</td>
                    <td className="px-4 py-3 text-[#4A453E]">{member.office || '-'}</td>
                    <td className="px-4 py-3 text-[#4A453E]">{member.primary_commandery || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs capitalize ${
                        member.status === 'active' ? 'bg-green-100 text-green-700' :
                        member.status === 'inactive' ? 'bg-gray-100 text-gray-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {member.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => setViewingMember(member)}
                          className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                          data-testid={`view-member-${member.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          onClick={() => handleEdit(member)}
                          className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                          data-testid={`edit-member-${member.id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(member)}
                          className="p-2 hover:bg-red-50 text-red-600"
                          data-testid={`delete-member-${member.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMembers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[#8A847A]">
                      No members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingMember ? 'Edit Member' : 'Add New Member'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="member-form">
              {/* Photo Upload for existing members */}
              {editingMember && (
                <div className="flex items-center gap-6 pb-4 border-b border-[#D8D2C4]">
                  <div className="relative">
                    <div className="w-24 h-24 bg-[#F5F2EB] flex items-center justify-center text-[#8A847A] overflow-hidden">
                      {editingMember.photo_url ? (
                        <img 
                          src={`${API}${editingMember.photo_url}?t=${Date.now()}`} 
                          alt={editingMember.full_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={40} />
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={photoInputRef}
                      onChange={(e) => handlePhotoUpload(e, editingMember.id)}
                    />
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#7B1818] text-white rounded-full flex items-center justify-center hover:bg-[#5C1212] transition-colors"
                    >
                      {uploadingPhoto ? <span className="spinner-sm" /> : <Camera size={16} />}
                    </button>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-[#8A847A] mb-2">Member Photo</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="rounded-none"
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                  <User size={16} /> Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title Prefix</label>
                    <Input
                      value={formData.title_prefix}
                      onChange={(e) => setFormData({...formData, title_prefix: e.target.value})}
                      placeholder="Sir, Dame, H.E."
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Full Name *</label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      required
                      className="rounded-none"
                      data-testid="member-input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date of Birth</label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nationality</label>
                    <Input
                      value={formData.nationality}
                      onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Occupation</label>
                    <Input
                      value={formData.occupation}
                      onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                      <SelectTrigger className="rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Order Information */}
              <div>
                <h3 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                  <Medal size={16} /> Order Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Rank</label>
                    <Select value={formData.rank} onValueChange={(v) => setFormData({...formData, rank: v})}>
                      <SelectTrigger className="rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RANKS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Office</label>
                    <Input
                      value={formData.office}
                      onChange={(e) => setFormData({...formData, office: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Commandery</label>
                    <Select 
                      value={formData.primary_commandery} 
                      onValueChange={(v) => setFormData({...formData, primary_commandery: v})}
                    >
                      <SelectTrigger className="rounded-none">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {commanderies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Prefecture</label>
                    <Select 
                      value={formData.primary_prefecture} 
                      onValueChange={(v) => setFormData({...formData, primary_prefecture: v})}
                    >
                      <SelectTrigger className="rounded-none">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {prefectories.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Investiture Date</label>
                    <Input
                      type="date"
                      value={formData.investiture_date}
                      onChange={(e) => setFormData({...formData, investiture_date: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                  <Phone size={16} /> Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="rounded-none"
                      data-testid="member-input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Emergency Contact Name</label>
                    <Input
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Emergency Contact Phone</label>
                    <Input
                      value={formData.emergency_phone}
                      onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
                      className="rounded-none"
                    />
                  </div>
                </div>
              </div>

              {/* User Account Section - Only for new members */}
              {!editingMember && (
                <div className="border border-[#D8D2C4] p-4 bg-[#F5F2EB]/50">
                  <h3 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                    <Key size={16} /> Portal Access
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="create_account"
                        checked={formData.create_account}
                        onChange={(e) => setFormData({...formData, create_account: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <label htmlFor="create_account" className="text-sm font-medium">
                        Create portal login account for this member
                      </label>
                    </div>
                    {formData.create_account && (
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div>
                          <label className="block text-sm font-medium mb-1">Password *</label>
                          <Input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            required={formData.create_account}
                            placeholder="Min 8 characters"
                            className="rounded-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">System Role *</label>
                          <Select 
                            value={formData.system_role} 
                            onValueChange={(v) => setFormData({...formData, system_role: v})}
                          >
                            <SelectTrigger className="rounded-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SYSTEM_ROLES.map(r => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-[#8A847A]">
                            <strong>Note:</strong> Email address will be used as login username. 
                            Grand Officers should typically have Admin access.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CV Upload for existing members */}
              {editingMember && (
                <div>
                  <h3 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                    <FileText size={16} /> Documents
                  </h3>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      ref={cvInputRef}
                      onChange={(e) => handleCVUpload(e, editingMember.id)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => cvInputRef.current?.click()}
                      disabled={uploadingCV}
                      className="rounded-none"
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingCV ? 'Uploading...' : 'Upload CV'}
                    </Button>
                    {editingMember.cv_url && (
                      <a
                        href={`${API}${editingMember.cv_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#7B1818] hover:underline"
                      >
                        <Download size={16} />
                        Download Current CV
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Biography & Notes */}
              <div>
                <h3 className="text-sm font-semibold text-[#1A1614] mb-3">Additional Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Biography</label>
                    <Textarea
                      value={formData.biography}
                      onChange={(e) => setFormData({...formData, biography: e.target.value})}
                      rows={3}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Internal Notes</label>
                    <Textarea
                      value={formData.internal_notes}
                      onChange={(e) => setFormData({...formData, internal_notes: e.target.value})}
                      rows={2}
                      className="rounded-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#D8D2C4]">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="member-submit-button">
                  {editingMember ? 'Update Member' : 'Create Member'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Enhanced View Modal */}
        <Dialog open={!!viewingMember} onOpenChange={() => setViewingMember(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Member Profile</DialogTitle>
            </DialogHeader>
            {viewingMember && (
              <div className="space-y-6" data-testid="member-details">
                {/* Header Section */}
                <div className="flex items-start gap-6 pb-6 border-b border-[#D8D2C4]">
                  <div className="relative">
                    <div className="w-28 h-28 bg-[#F5F2EB] flex items-center justify-center text-[#8A847A] overflow-hidden">
                      {viewingMember.photo_url ? (
                        <img 
                          src={`${API}${viewingMember.photo_url}`} 
                          alt={viewingMember.full_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={48} />
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={photoInputRef}
                      onChange={(e) => handlePhotoUpload(e, viewingMember.id)}
                    />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#7B1818] text-white rounded-full flex items-center justify-center hover:bg-[#5C1212] transition-colors"
                      title="Upload photo"
                    >
                      {uploadingPhoto ? <span className="spinner-sm" /> : <Camera size={16} />}
                    </button>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-2xl text-[#1A1614]">
                      {viewingMember.title_prefix && `${viewingMember.title_prefix} `}{viewingMember.full_name}
                    </h3>
                    <p className="text-lg text-[#7B1818] font-medium">{viewingMember.rank}</p>
                    {viewingMember.office && (
                      <p className="text-[#4A453E]">{viewingMember.office}</p>
                    )}
                    <span className={`inline-block mt-2 px-3 py-1 text-sm capitalize ${
                      viewingMember.status === 'active' ? 'bg-green-100 text-green-700' :
                      viewingMember.status === 'inactive' ? 'bg-gray-100 text-gray-700' :
                      viewingMember.status === 'emeritus' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {viewingMember.status}
                    </span>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                    <Phone size={16} className="text-[#7B1818]" /> Contact Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Envelope size={16} className="text-[#8A847A]" />
                      <span className="text-[#8A847A]">Email:</span>
                      {viewingMember.email ? (
                        <a href={`mailto:${viewingMember.email}`} className="text-[#7B1818] hover:underline">
                          {viewingMember.email}
                        </a>
                      ) : '-'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-[#8A847A]" />
                      <span className="text-[#8A847A]">Phone:</span>
                      <span>{viewingMember.phone || '-'}</span>
                    </div>
                    <div className="col-span-2 flex items-start gap-2">
                      <MapPin size={16} className="text-[#8A847A] mt-0.5" />
                      <span className="text-[#8A847A]">Address:</span>
                      <span>{viewingMember.address || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                    <User size={16} className="text-[#7B1818]" /> Personal Information
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-[#8A847A]">Date of Birth:</span>
                      <p className="font-medium">{viewingMember.date_of_birth || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[#8A847A]">Nationality:</span>
                      <p className="font-medium">{viewingMember.nationality || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[#8A847A]">Occupation:</span>
                      <p className="font-medium">{viewingMember.occupation || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                    <Medal size={16} className="text-[#7B1818]" /> Order Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Buildings size={16} className="text-[#8A847A]" />
                      <span className="text-[#8A847A]">Commandery:</span>
                      <span>{viewingMember.primary_commandery || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Buildings size={16} className="text-[#8A847A]" />
                      <span className="text-[#8A847A]">Prefecture:</span>
                      <span>{viewingMember.primary_prefecture || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarBlank size={16} className="text-[#8A847A]" />
                      <span className="text-[#8A847A]">Investiture Date:</span>
                      <span>{viewingMember.investiture_date || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase size={16} className="text-[#8A847A]" />
                      <span className="text-[#8A847A]">Participation:</span>
                      <span className="capitalize">{viewingMember.participation_level || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                {(viewingMember.emergency_contact || viewingMember.emergency_phone) && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1A1614] mb-3">Emergency Contact</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm bg-red-50 p-3 border border-red-100">
                      <div>
                        <span className="text-[#8A847A]">Name:</span>
                        <p className="font-medium">{viewingMember.emergency_contact || '-'}</p>
                      </div>
                      <div>
                        <span className="text-[#8A847A]">Phone:</span>
                        <p className="font-medium">{viewingMember.emergency_phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Biography */}
                {viewingMember.biography && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#1A1614] mb-2">Biography</h4>
                    <p className="text-[#4A453E] text-sm whitespace-pre-wrap">{viewingMember.biography}</p>
                  </div>
                )}

                {/* Documents */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1A1614] mb-3 flex items-center gap-2">
                    <FileText size={16} className="text-[#7B1818]" /> Documents
                  </h4>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      ref={cvInputRef}
                      onChange={(e) => handleCVUpload(e, viewingMember.id)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => cvInputRef.current?.click()}
                      disabled={uploadingCV}
                      className="rounded-none"
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingCV ? 'Uploading...' : 'Upload CV'}
                    </Button>
                    {viewingMember.cv_url ? (
                      <a
                        href={`${API}${viewingMember.cv_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F2EB] text-sm text-[#4A453E] hover:bg-[#E8E4DB] transition-colors"
                      >
                        <FileText size={16} />
                        {viewingMember.cv_filename || 'Download CV'}
                        <Download size={14} />
                      </a>
                    ) : (
                      <span className="text-sm text-[#8A847A]">No CV uploaded</span>
                    )}
                  </div>
                </div>

                {/* Internal Notes */}
                {viewingMember.internal_notes && (
                  <div className="bg-[#F5F2EB] p-4">
                    <h4 className="text-sm font-semibold text-[#1A1614] mb-2">Internal Notes</h4>
                    <p className="text-[#4A453E] text-sm whitespace-pre-wrap">{viewingMember.internal_notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-[#D8D2C4]">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setViewingMember(null);
                      handleEdit(viewingMember);
                    }}
                    className="rounded-none"
                  >
                    <PencilSimple size={16} className="mr-2" />
                    Edit Member
                  </Button>
                  <Button
                    onClick={() => setViewingMember(null)}
                    className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MembersPage;
